import { useEffect, useRef } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { cn } from '@xdocs/ui';
import { useEditorT } from './useEditorT.js';
import styles from './ManuscriptEditor.module.css';

export interface ManuscriptEditorProps {
  /** Title text (plain text, not HTML). */
  title: string;
  /** Body HTML — server-side sanitized (`isomorphic-dompurify`) before
   * persist (CODE_GUIDELINES §26: "Any HTML that originates from a WYSIWYG
   * editor ... is server-side sanitized on write"); this surface never
   * re-sanitizes client-side, it only reflects the owner/editor's own
   * live-typed content back to them. */
  body: string;
  onTitleChange: (title: string) => void;
  onBodyChange: (body: string) => void;
  /**
   * Bump this (e.g. `${chapterId}:${resetNonce}`) whenever `title`/`body`
   * change from an EXTERNAL source (switching chapters, `restoreDraft()`,
   * a post-conflict `resetContent()`) so the DOM is re-seeded. Must NOT
   * change on ordinary typing — this component owns its own DOM content
   * between resets (the classic React-controlled-contenteditable trap:
   * reflecting every keystroke back through `dangerouslySetInnerHTML`
   * fights the caret position).
   */
  resetKey: string;
  readOnly?: boolean;
  className?: string | undefined;
}

/**
 * F67 WYSIWYG manuscript surface (§28.1) — a `contenteditable`-based editor
 * (no rich-text lib on the E13 allowlist) over one chapter's title + body,
 * paired with `EditorToolbar` (targets whichever region has focus via
 * `document.execCommand`). `readOnly` renders the same content statically
 * (F67 AC: "reader-role grantee gets the read-only view, never a dead/
 * disabled editor" — this is that static fallback; the full F66 reader
 * surface with pagination/font/theme controls is a separate route owned by
 * WS-EDITOR's reader slice).
 */
export function ManuscriptEditor({ title, body, onTitleChange, onBodyChange, resetKey, readOnly = false, className }: ManuscriptEditorProps): ReactElement {
  const { t } = useEditorT();
  const titleRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (titleRef.current) titleRef.current.textContent = title;
    if (bodyRef.current) bodyRef.current.innerHTML = body;
    // `title`/`body` deliberately excluded from the dependency array —
    // resync ONLY on an external reset (`resetKey`), never on every
    // keystroke (see the prop doc above); this effect always reads the
    // CURRENT `title`/`body` closure values, it just shouldn't re-RUN
    // because of them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  function handleTitleInput(event: FormEvent<HTMLDivElement>): void {
    onTitleChange(event.currentTarget.textContent ?? '');
  }

  function handleBodyInput(event: FormEvent<HTMLDivElement>): void {
    onBodyChange(event.currentTarget.innerHTML);
  }

  return (
    <article className={cn(styles['page'], className)}>
      {readOnly && (
        <p className={styles['readOnlyNotice']} role="status">
          {t('editor:readOnly.title')}
        </p>
      )}
      {
        // Both regions' visible text is injected imperatively (`textContent`/
        // `innerHTML` in the effect above), not as JSX children — the
        // classic contenteditable-in-React pattern (see the `resetKey` prop
        // doc). Both are plain `<div>`s (not a semantic `<h1>`) carrying
        // `role="textbox"` — the conventional accessible shape for a
        // contenteditable region; a true heading role and an editable-
        // textbox role can't coexist on one element (ARIA forbids dual
        // roles), and for a bidirectional EDIT surface (vs. the read-only
        // F66 reader), textbox semantics matter more to AT users actually
        // typing here than heading-navigation does.
      }
      <div
        ref={titleRef}
        className={styles['title']}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="false"
        aria-readonly={readOnly}
        aria-label={t('editor:content.titleLabel')}
        onInput={readOnly ? undefined : handleTitleInput}
      />
      <div
        ref={bodyRef}
        className={styles['body']}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-readonly={readOnly}
        aria-label={t('editor:content.bodyLabel')}
        onInput={readOnly ? undefined : handleBodyInput}
      />
    </article>
  );
}
